import { Link } from 'react-router-dom';

import { Badge, type BadgeTone } from '../../shared/ui/Badge';
import { ConfirmInline } from '../../shared/ui/ConfirmInline';

import type { OpenAttemptKind, OpenAttemptRow } from './continue-api';
import {
  blockedReason,
  canEnter,
  formatLastActive,
  formatTimeLeft,
  KIND_LABEL,
  resumePath,
} from './open-attempts';
import s from './ResumeAttemptRow.module.css';

const KIND_TONE: Record<OpenAttemptKind, BadgeTone> = {
  scheduled: 'info',
  practice: 'neutral',
  assignment: 'warning',
};

const SECTION_LABEL: Record<string, string> = {
  listening: 'Listening',
  reading: 'Reading',
  writing: 'Writing',
  speaking: 'Speaking',
};

interface Props {
  row: OpenAttemptRow;
  /** Home-card density: title, one meta line, one action. */
  compact?: boolean;
  /** Discard is offered only where it makes sense — practice, on the full
   *  list. Omit and the control is not rendered at all. */
  onDiscard?: (() => void) | undefined;
  discarding?: boolean;
  confirmingDiscard?: boolean;
  onDiscardCancel?: (() => void) | undefined;
}

/**
 * One unfinished test, as a row the student can get back into.
 *
 * Three tiers, in the order someone scans them: what it is (title + kind), what
 * state it is in (section, answered, time left), and when they last touched it.
 * Resume is the only primary action — discarding is rare and destructive, so it
 * sits behind an in-place confirmation rather than a modal, which would hide
 * the very row being acted on.
 */
export function ResumeAttemptRow({
  row,
  compact = false,
  onDiscard,
  discarding = false,
  confirmingDiscard = false,
  onDiscardCancel,
}: Props): JSX.Element {
  const blocked = blockedReason(row);
  const sectionName =
    row.current_section_kind === null
      ? null
      : // eslint-disable-next-line security/detect-object-injection -- key comes from the section enum, not user input
        (SECTION_LABEL[row.current_section_kind] ?? null);

  const progress =
    row.question_count > 0
      ? `${String(row.answered_count)} of ${String(row.question_count)} answered`
      : null;
  const place =
    row.section_count > 1
      ? `Section ${String(row.section_index)} of ${String(row.section_count)}`
      : null;

  const meta = [place, sectionName, progress].filter((x): x is string => x !== null).join(' · ');

  return (
    <li className={compact ? `${s.root} ${s.compact}` : s.root}>
      <div className={s.main}>
        <div className={s.titleRow}>
          <Badge tone={KIND_TONE[row.kind]}>{KIND_LABEL[row.kind]}</Badge>
          <h3 className={s.title}>{row.test_title}</h3>
        </div>
        <p className={s.meta}>{meta === '' ? 'Not started yet' : meta}</p>
        {!compact ? (
          <p className={s.subMeta}>Last worked on {formatLastActive(row.last_active_at)}</p>
        ) : null}
      </div>

      <div className={s.side}>
        <span className={row.remaining_ms <= 5 * 60_000 ? s.timeLow : s.time}>
          {formatTimeLeft(row.remaining_ms)}
        </span>
        {blocked !== null ? <p className={s.blocked}>{blocked}</p> : null}
        {canEnter(row) ? (
          <Link to={resumePath(row)} className={s.resume}>
            {row.remaining_ms <= 0 ? 'Go in to submit →' : 'Continue →'}
          </Link>
        ) : null}
        {onDiscard !== undefined && !confirmingDiscard ? (
          <button type="button" className={s.discard} onClick={onDiscard}>
            Discard
          </button>
        ) : null}
      </div>

      {confirmingDiscard && onDiscard !== undefined && onDiscardCancel !== undefined ? (
        <div className={s.confirmRow}>
          <ConfirmInline
            message={`Delete this attempt of “${row.test_title}”? Your answers are erased and cannot be recovered.`}
            confirmLabel="Delete attempt"
            cancelLabel="Keep it"
            busy={discarding}
            onConfirm={onDiscard}
            onCancel={onDiscardCancel}
          />
        </div>
      ) : null}
    </li>
  );
}
